/**
 * Table - сучасний компонент таблиці на базі UIManager
 *
 * Підтримує:
 * - Інтеграцію з CrudResourceManager
 * - Події життєвого циклу (onInit, onBuildProps, onBuildColumns, etc.)
 * - Пагінацію та завантаження даних
 */

import * as React from 'react'

import { useProfiler } from '@/Core/Utils/profilerHelpers'

import { toJS } from 'mobx'
import type { TableProps } from './Table.types'
import { createTableContext, TableContextProvider } from './TableContextProvider'
import { DeepCloneTask } from '@/Core/Tasks/Utils/DeepCloneTask';

/**
 * Компонент Table
 */
export const Table: React.FC<TableProps> = ({ tableContext, ...tableProps }) => {
    /** Інтеграція з профайлером в dev режимі */
    const { addEvent, setStatus } = useProfiler('Table', 'component', '/Core/UI/Table/Table.tsx', [tableProps])
    
    React.useEffect(() => {
        setStatus('active')
        addEvent('initialized', { props: tableProps })
    }, [])
    
    /** Створюємо або використовуємо переданий tableContext */
    if( !tableContext ) {
        tableContext = createTableContext({ ...tableProps })
    }
    
    /** Оновлюємо props при кожному рендері (конвертуємо MobX observables в plain JS) */
    tableContext.mergeProps({
        ...tableProps,
        dataSource: toJS(tableProps.dataSource),
        pagination: toJS(tableProps.pagination),
    })
    
    /** Оновлюємо storages при кожному рендері */
    if (tableProps.storages) {
        tableContext.mergeStorages(tableProps.storages)
    }
    
    const eventManager = tableContext.getEventManager()
    
    /** Emit події для колонок */
    eventManager.emit('onBuildColumns', { columns: tableContext.props?.columns ?? [], tableContext })

    if (Array.isArray(tableContext.props?.columns)) {
        tableContext.props.columns.forEach((column: any) => {
            eventManager.emit('onBuildColumn', { column, tableContext })
        })
    }

    /** Emit події для dataSource */
    eventManager.emit('onBuildDataSourceItems', { items: tableContext.props?.dataSource ?? [], tableContext })

    if (Array.isArray(tableContext.props?.dataSource)) {
        tableContext.props.dataSource.forEach((item: any) => {
            item._original = DeepCloneTask(item);
            eventManager.emit('onBuildDataSourceItem', { item, tableContext })
        })
    }

    addEvent('render', { timestamp: Date.now() })

    return (
        <TableContextProvider value={tableContext}>
            {tableContext.render()}
        </TableContextProvider>
    )
}

export default Table
